import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import TasksLayout from "../../components/TaskDetailsLayouts/TasksLayout";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { CheckIcon, WarningIcon } from "../../components/tasks/Icons";
import { FiEye, FiEdit3 } from "react-icons/fi";

const ReviewerTaskDetails = () => {
  const [taskID, setTaskID] = useState("");
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(null);
  const [language, setLanguage] = useState("");
  const [category, setCategory] = useState("");
  const [technicalCategory, setTechnicalCategory] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRtl, setIsRtl] = useState(false);
  const [isArabicMSA, setIsArabicMSA] = useState(false);

  // Markdown preview toggle states
  const [promptMarkdownPreview, setPromptMarkdownPreview] = useState(false);
  const [responseMarkdownPreview, setResponseMarkdownPreview] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();

  const languageOptions = [
    { display: "Python", value: "python" },
    { display: "JavaScript", value: "javascript" },
    { display: "Java", value: "java" },
    { display: "C#", value: "csharp" },
    { display: "Dart", value: "dart" },
    { display: "PHP", value: "php" },
    { display: "Ruby", value: "ruby" },
    { display: "Rust", value: "rust" },
    { display: "Kotlin", value: "kotlin" },
    { display: "Scala", value: "scala" },
    { display: "Erlang", value: "erlang" },
    { display: "Elixir", value: "elixir" },
    { display: "Swift", value: "swift" },
    { display: "Go", value: "go" },
    { display: "C++", value: "c++" },
    { display: "C", value: "c" },
    { display: "TypeScript", value: "typescript" }
  ];

  const taskCategoryOptions = [
    { display: "Tourism", value: "tourism" },
    { display: "Sports", value: "sports" },
    { display: "Games", value: "games" },
    { display: "Health", value: "health" },
    { display: "Finance", value: "finance" }
  ];

  const technicalCategoryOptions = [
    { display: "Code Debugging", value: "debugging" },
    { display: "Code Generation", value: "generation" },
    { display: "Code Understanding", value: "understanding" }
  ];

  // Markdown components for rendering
  const markdownComponents = {
    h1: ({ node, ...props }) => <h1 className="text-xl font-bold my-4 text-gray-800 dark:text-gray-200" {...props} />,
    h2: ({ node, ...props }) => <h2 className="text-lg font-bold my-3 text-gray-800 dark:text-gray-200" {...props} />,
    h3: ({ node, ...props }) => <h3 className="text-md font-bold my-2 text-gray-800 dark:text-gray-200" {...props} />,
    h4: ({ node, ...props }) => <h4 className="text-base font-bold my-2 text-gray-800 dark:text-gray-200" {...props} />,
    h5: ({ node, ...props }) => <h5 className="text-sm font-bold my-1 text-gray-800 dark:text-gray-200" {...props} />,
    h6: ({ node, ...props }) => <h6 className="text-xs font-bold my-1 text-gray-800 dark:text-gray-200" {...props} />,
    p: ({ node, ...props }) => <p className="my-2 text-gray-800 dark:text-gray-200" {...props} />,
    a: ({ node, ...props }) => <a className="text-blue-600 hover:underline dark:text-blue-400" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 text-gray-800 dark:text-gray-200" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 text-gray-800 dark:text-gray-200" {...props} />,
    li: ({ node, ...props }) => <li className="my-1 text-gray-800 dark:text-gray-200" {...props} />,
    blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 italic my-2 text-gray-700 dark:text-gray-300" {...props} />,
    table: ({ node, ...props }) => <div className="overflow-x-auto my-2"><table className="min-w-full border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200" {...props} /></div>,
    thead: ({ node, ...props }) => <thead className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200" {...props} />,
    tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-300 dark:divide-gray-700 text-gray-800 dark:text-gray-200" {...props} />,
    tr: ({ node, ...props }) => <tr className="hover:bg-gray-100 dark:hover:bg-gray-750 text-gray-800 dark:text-gray-200" {...props} />,
    th: ({ node, ...props }) => <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r last:border-r-0 border-gray-300 dark:border-gray-700" {...props} />,
    td: ({ node, ...props }) => <td className="px-3 py-2 whitespace-normal text-sm border-r last:border-r-0 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200" {...props} />,
    hr: ({ node, ...props }) => <hr className="my-4 border-t border-gray-300 dark:border-gray-700" {...props} />,
    img: ({ node, ...props }) => <img className="max-w-full h-auto my-2 rounded" {...props} />,
    pre: ({ node, ...props }) => <pre className="rounded-md my-2 bg-gray-800 text-white overflow-x-auto" {...props} />,
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <div className="overflow-x-auto rounded-md my-2">
          <SyntaxHighlighter
            style={atomDark}
            showLineNumbers={true}
            language={match[1]}
            PreTag="div"
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        </div>
      ) : (
        <code className={`${inline ? 'px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' : ''} ${className || ''}`} {...props}>
          {children}
        </code>
      );
    }
  };

  // Toast notification
  const showToast = (message, type = 'success') => {
    Swal.fire({
      icon: type,
      title: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const getTaskDetails = async () => {
    try {
      const response = await apiClient.get(`/api/tasks/${id}/`);
      setTaskID(response.data.id);
      setPrompt(response.data.prompt);
      setResponse(response.data.response);
      setLanguage(response.data.language);
      setCategory(response.data.category);
      setTechnicalCategory(response.data.technical_category || "");
      setIsArabicMSA(response.data.is_arabic_msa);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        showToast("ID Doesn't Match Any Task.", "error");
        navigate("/dashboard");
      } else if (error.response && error.response.status === 403) {
        showToast(error.response.data.message, "error");
        navigate("/dashboard");
      } else if (error.response && error.response.status === 400) {
        showToast(error.response.data.message, "error");
        navigate("/dashboard");
      } else {
        showToast("An unexpected error occurred.", "error");
        navigate("/dashboard");
        console.error("An error occurred", error);
      }
    }
  };

  useEffect(() => {
    getTaskDetails();
  }, [id]);

  const handleUnEnrollClick = async () => {
    try {
      const result = await Swal.fire({
        title: 'Skip this task?',
        text: "You won't be able to review this task anymore.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, skip it!'
      });

      if (result.isConfirmed) {
        const response = await apiClient.post(`/api/tasks/${id}/skip/`);
        if (response.status === 200) {
          showToast("Task successfully skipped.");
          navigate("/dashboard");
        } else {
          showToast(response.data.error || "Failed to skip the task.", "error");
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || "An error occurred while skipping the task.";
      showToast(errorMessage, "error");
    }
  };

  // Function to check if we can submit
  const validateSubmission = () => {
    if (!rating) {
      showToast("Please select a rating.", "error");
      return false;
    }

    if (!feedback || feedback.trim().length < 10) {
      showToast("Please provide meaningful feedback (at least 10 characters).", "error");
      return false;
    }
    
    if (!technicalCategory) {
      showToast("Please select a technical category.", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async (accept = true) => {
    if (!validateSubmission()) return;

    // Enforce rating-based accept/reject logic
    if (rating <= 2 && accept) {
      showToast("Tasks with ratings 1 or 2 can only be rejected.", "error");
      return;
    }
    
    if (rating >= 3 && !accept) {
      showToast("Tasks with ratings 3, 4, or 5 can only be accepted.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiResponse = await apiClient.post(`/api/tasks/${id}/reviews/`, {
        prompt,
        response,
        language,
        category,
        technical_category: technicalCategory || null,
        feedback,
        rate: rating,
        accepted: accept,
        is_arabic_msa: isArabicMSA,
      });

      if (apiResponse.status === 201) {
        showToast(accept ? "Task accepted and review submitted!" : "Task rejected and feedback submitted!");
        navigate("/submitted");
      } else {
        showToast("Failed to submit review.", "error");
      }
    } catch (error) {
      showToast("An error occurred while submitting the review.", "error");
      console.error("Review submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Word count function
  const wordCount = (text) => {
    return text.trim().split(/\s+/).length;
  };

  const getRatingColor = (rate) => {
    if (rate === rating) {
      if (rate === 5) return "bg-gradient-to-r from-cyan-500 to-blue-600 text-white";
      if (rate === 4) return "bg-blue-500 text-white";
      if (rate === 3) return "bg-green-500 text-white";
      if (rate === 2) return "bg-orange-500 text-white";
      if (rate === 1) return "bg-red-500 text-white";
    }
    return "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
  };

  const getRatingLabel = (rate) => {
    if (rate === 5) return "Excellent";
    if (rate === 4) return "Very Good";
    if (rate === 3) return "Good";
    if (rate === 2) return "Fair";
    if (rate === 1) return "Poor";
    return "";
  };

  const handleAccept = () => handleSubmit(true);
  const handleReject = () => handleSubmit(false);

  return (
    <TasksLayout
      handleSubmit={handleAccept}
      handleUnEnrollClick={handleUnEnrollClick}
      handleReject={handleReject}
      rating={rating}
    >
      <div className="p-4 pt-1 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
        <div className="mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                  {isRtl ? "مراجعة المهمة" : "Review Task"}
                </h1>
                <div className="flex items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {isRtl ? "معرف المهمة:" : "Task ID:"} <span className="font-medium">{taskID}</span>
                  </span>
                  <span className="mx-2 text-gray-400">•</span>
                  <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300">
                    {category ? taskCategoryOptions.find(option => option.value === category)?.display : (isRtl ? 'غير مصنف' : 'Uncategorized')}
                  </span>
                </div>
              </div>
              
              {/* RTL Toggle */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="rtl-toggle" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {isRtl ? "وضع RTL" : "RTL Mode"}
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="rtl-toggle"
                      checked={isRtl}
                      onChange={() => setIsRtl(!isRtl)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 dark:peer-focus:ring-cyan-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Task Details Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
            <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
              <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {isRtl ? "تفاصيل المهمة" : "Task Details"}
              </h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {isRtl ? "لغة البرمجة" : "Programming Language"}
                  </label>
                  <div className="relative">
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="block w-full px-4 py-2.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600"
                    >
                      <option value="" disabled>{isRtl ? "اختر لغة البرمجة" : "Select a programming language"}</option>
                      {languageOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {isRtl ? "فئة المهمة" : "Task Category"}
                  </label>
                  <div className="relative">
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="block w-full px-4 py-2.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600"
                    >
                      <option value="" disabled>{isRtl ? "اختر فئة" : "Select a category"}</option>
                      {taskCategoryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isRtl ? "الفئة التقنية" : "Technical Category"} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={technicalCategory}
                    onChange={(e) => setTechnicalCategory(e.target.value)}
                    className="block w-full px-4 py-2.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600"
                  >
                    <option value="" disabled>{isRtl ? "اختر فئة تقنية" : "Select a technical category"}</option>
                    {technicalCategoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.display}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {isRtl ? "اختر نوع المهمة البرمجية" : "Choose the type of coding task"}
                </p>
              </div>

              <div className="mt-6">
                <label className="inline-flex items-center dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={isArabicMSA}
                    onChange={() => setIsArabicMSA(!isArabicMSA)}
                    className="h-5 w-5 rounded text-cyan-600 focus:ring-cyan-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-offset-gray-800"
                  />
                  <span className="ml-2 flex items-center">
                    {isRtl ? "اللغة العربية" : "Arabic"}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Prompt Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
            <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
              <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {isRtl ? "نص المهمة" : "Task Prompt"}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isRtl ? "تحرير نص المهمة" : "Edit Task Prompt"}
                </label>
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={promptMarkdownPreview}
                      onChange={() => setPromptMarkdownPreview(!promptMarkdownPreview)}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    {promptMarkdownPreview ? (
                      <>
                        <FiEye className="w-4 h-4" />
                        {isRtl ? "معاينة" : "Preview"}
                      </>
                    ) : (
                      <>
                        <FiEdit3 className="w-4 h-4" />
                        {isRtl ? "تحرير" : "Edit"}
                      </>
                    )}
                  </span>
                </div>
              </div>
              
              {/* Prompt Input/Preview */}
              <div className="relative">
                {!promptMarkdownPreview ? (
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className={`w-full h-48 px-3 py-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 bg-white dark:bg-gray-800 transition-colors duration-200 resize-none ${isRtl ? 'text-right' : 'text-left'}`}
                    dir={isRtl ? "rtl" : "ltr"}
                  />
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/50 overflow-auto">
                    {prompt.trim() ? (
                      <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {prompt}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">
                        {isRtl ? "لا يوجد محتوى للمعاينة" : "No content to preview"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Response Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                </svg>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                  {isRtl ? "الاستجابة المتوقعة" : "Expected Response"}
                </h2>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                {wordCount(response)} {isRtl ? "كلمة" : "words"}
              </span>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isRtl ? "تحرير الاستجابة المتوقعة" : "Edit Expected Response"}
                </label>
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={responseMarkdownPreview}
                      onChange={() => setResponseMarkdownPreview(!responseMarkdownPreview)}
                      className="sr-only peer" 
                    />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                 </label>
                 <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                   {responseMarkdownPreview ? (
                     <>
                       <FiEye className="w-4 h-4" />
                       {isRtl ? "معاينة" : "Preview"}
                     </>
                   ) : (
                     <>
                       <FiEdit3 className="w-4 h-4" />
                       {isRtl ? "تحرير" : "Edit"}
                     </>
                   )}
                 </span>
               </div>
             </div>
             
             {/* Response Input/Preview */}
             <div className="relative">
               {!responseMarkdownPreview ? (
                 <textarea
                   value={response}
                   onChange={(e) => setResponse(e.target.value)}
                   className={`w-full h-48 px-3 py-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 bg-white dark:bg-gray-800 transition-colors duration-200 resize-none ${isRtl ? 'text-right' : 'text-left'}`}
                   dir={isRtl ? "rtl" : "ltr"}
                 />
               ) : (
                 <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/50 overflow-auto">
                   {response.trim() ? (
                     <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                       <ReactMarkdown
                         remarkPlugins={[remarkGfm]}
                         components={markdownComponents}
                       >
                         {response}
                       </ReactMarkdown>
                     </div>
                   ) : (
                     <p className="text-gray-500 dark:text-gray-400 italic">
                       {isRtl ? "لا يوجد محتوى للمعاينة" : "No content to preview"}
                     </p>
                   )}
                 </div>
               )}
             </div>
           </div>
         </div>

         {/* Review Section */}
         <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
           <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
             <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
             </svg>
             <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
               {isRtl ? "مراجعتك" : "Your Review"}
             </h2>
           </div>
           <div className="p-6 space-y-6">
             {/* Rating */}
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                 {isRtl ? "التقييم" : "Rating"} <span className="text-red-500">*</span>
               </label>
               <div className="flex flex-wrap items-center justify-center gap-2">
                 {[1, 2, 3, 4, 5].map((rate) => (
                   <button
                     key={rate}
                     onClick={() => setRating(rate)}
                     className={`flex items-center justify-center w-14 h-14 rounded-full text-lg font-medium transition-all duration-200 hover:shadow-md ${getRatingColor(rate)}`}
                     aria-label={`Rate ${rate} - ${getRatingLabel(rate)}`}
                   >
                     {rate}
                   </button>
                 ))}
               </div>
               <div className="mt-3 flex flex-col items-center justify-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
                 {rating ? (
                   <>
                     <div className="flex items-center px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700">
                       <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
                       <span>
                         {isRtl ? "الاختيار:" : "Selected:"} <span className="font-medium">{getRatingLabel(rating)}</span>
                       </span>
                     </div>
                     {rating <= 2 && (
                       <div className="flex items-center px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/20">
                         <WarningIcon className="w-4 h-4 text-red-500 mr-1" />
                         <span className="text-red-700 dark:text-red-400">
                           {isRtl ? "مع هذا التقييم، يمكنك فقط رفض المهمة" : "With this rating, you can only reject the task"}
                         </span>
                       </div>
                     )}
                     {rating >= 3 && (
                       <div className="flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/20">
                         <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
                         <span className="text-green-700 dark:text-green-400">
                           {isRtl ? "مع هذا التقييم، يمكنك فقط قبول المهمة" : "With this rating, you can only accept the task"}
                         </span>
                       </div>
                     )}
                   </>
                 ) : (
                   <div className="flex items-center px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/20">
                     <WarningIcon className="w-4 h-4 text-amber-500 mr-1" />
                     <span>{isRtl ? "الرجاء اختيار تقييم" : "Please select a rating"}</span>
                   </div>
                 )}
               </div>
             </div>

             {/* Feedback */}
             <div>
               <div className="flex justify-between items-center mb-2">
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                   {isRtl ? "تعليقات مفصلة" : "Detailed Feedback"} <span className="text-red-500">*</span>
                 </label>
                 <span className={`text-sm px-2 py-1 rounded-full ${feedback.length < 10 ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'}`}>
                   {feedback.length} {isRtl ? "حرف (الحد الأدنى 10)" : "characters (min 10)"}
                 </span>
               </div>
               <textarea
                 value={feedback}
                 onChange={(e) => setFeedback(e.target.value)}
                 placeholder={isRtl 
                   ? "قدم تعليقات مفصلة حول جودة المهمة ووضوحها وأي اقتراحات للتحسين..."
                   : "Provide detailed feedback on the task quality, clarity, and any suggestions for improvement..."}
                 className={`w-full h-32 px-3 py-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 bg-white dark:bg-gray-800 transition-colors duration-200 ${isRtl ? 'text-right' : 'text-left'}`}
                 dir={isRtl ? "rtl" : "ltr"}
               />
             </div>
           </div>
         </div>

         {/* Final Preview Section - Shows both prompt and response side by side */}
         <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
           <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
             <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
               <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
               </svg>
               {isRtl ? "معاينة نهائية للمهمة" : "Final Task Preview"}
             </h2>
           </div>
           
           <div className="p-6 space-y-6">
             {/* Task metadata summary */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               <div>
                 <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{isRtl ? "لغة البرمجة" : "Programming Language"}</h3>
                 <p className="text-gray-800 dark:text-gray-200 font-medium">{languageOptions.find(l => l.value === language)?.display || language}</p>
               </div>
               <div>
                 <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{isRtl ? "فئة المهمة" : "Task Category"}</h3>
                 <p className="text-gray-800 dark:text-gray-200 font-medium">{taskCategoryOptions.find(c => c.value === category)?.display || category}</p>
               </div>
               <div>
                 <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{isRtl ? "الفئة التقنية" : "Technical Category"}</h3>
                 <p className="text-gray-800 dark:text-gray-200 font-medium">{technicalCategoryOptions.find(c => c.value === technicalCategory)?.display || technicalCategory}</p>
               </div>
             </div>

             {/* Full height preview sections */}
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               <div>
                 <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{isRtl ? "نص المهمة" : "Task Prompt"}</h3>
                 <div className="h-64 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto">
                   <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                     <ReactMarkdown
                       remarkPlugins={[remarkGfm]}
                       components={markdownComponents}
                     >
                       {prompt}
                     </ReactMarkdown>
                   </div>
                 </div>
               </div>
               
               <div>
                 <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{isRtl ? "الاستجابة المتوقعة" : "Expected Response"}</h3>
                 <div className="h-64 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto">
                   <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                     <ReactMarkdown
                       remarkPlugins={[remarkGfm]}
                       components={markdownComponents}
                     >
                       {response}
                     </ReactMarkdown>
                   </div>
                 </div>
               </div>
             </div>

             {/* Review summary */}
             {(rating || feedback) && (
               <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                 <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{isRtl ? "ملخص المراجعة" : "Review Summary"}</h3>
                 <div className="space-y-2">
                   {rating && (
                     <div className="flex items-center gap-2">
                       <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{isRtl ? "التقييم:" : "Rating:"}</span>
                       <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(rating)}`}>
                         {rating} - {getRatingLabel(rating)}
                       </span>
                     </div>
                   )}
                   {feedback && (
                     <div>
                       <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{isRtl ? "التعليقات:" : "Feedback:"}</span>
                       <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{feedback.length > 100 ? feedback.substring(0, 100) + "..." : feedback}</p>
                     </div>
                   )}
                 </div>
               </div>
             )}

             {isArabicMSA && (
               <div className="flex items-center">
                 <div className="px-3 py-1 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-xs font-medium rounded-full border border-cyan-200 dark:border-cyan-700">
                   {isRtl ? "اللغة العربية" : "Arabic"}
                 </div>
               </div>
             )}
           </div>
         </div>
       </div>
     </div>
   </TasksLayout>
 );
};

export default ReviewerTaskDetails;